/**
 * ================================================================================================
 *  נתונים ועדכונים — THE READINGS COLUMN, ON EVERY CUSTOMER SCREEN. **Owner, 2026-11-01.**
 * ================================================================================================
 *
 *     *"אתה עובר בין מסכים, הוויג׳טים לא אמורים להשתנות."*
 *
 * ── THEY WERE THE HOME SCREEN'S, AND THAT IS WHY THEY DISAPPEARED ─────────────────────────────
 *
 * `TenantHomeScreen` built this column and handed it to `AppShell` as its `rail` prop, so every
 * other screen passed nothing and the rail held only `בשימוש לאחרונה`. Opening the settings and
 * watching four cards vanish is what he is describing: a reading a person glances at is furniture,
 * and furniture does not belong to one room.
 *
 * ── SO THE SHELL OWNS THEM, AND WITH THEM THE SIDE REGION'S ORDER ─────────────────────────────
 *
 * The cards are draggable and that is a per-person order (§9), which means the state that holds
 * it has to live wherever the cards do. It moves here whole: `homeOrder('side')`, the hidden set,
 * the drag controller and the hydrate-from-account effect. The home screen keeps the MAIN region
 * and nothing about it changes.
 *
 * ── ONE READ, ON EVERY PAGE LOAD, AND IT IS THE CHEAPEST SHAPE AVAILABLE ──────────────────────
 *
 * `user.market.read` serves all five domains in one answer, so this is ONE request per load and
 * not five — and the same answer feeds the updates strip in the header, which is why `AppShell`
 * makes the call and hands the result to both. The read never asks a publisher: it serves what is
 * stored, and going out is the scheduler's `refresh()`.
 *
 * AND IT IS THE CUSTOMER SURFACE ONLY, like the rail it sits in. The platform console has no such
 * column and does not grow one.
 */

import { useCallback, useEffect, useState } from 'react';
import type { JSX } from 'react';

import { MarketTile } from '../components/MarketTile';
import type { ServerData } from '../api/useServerData';
import type { MarketCardView } from '../api/contract';
import { useLocale } from '../i18n/LocaleProvider';
import { Tile } from './Tile';
import {
  hiddenTiles,
  homeOrder,
  moveTile,
  placeTileAt,
  TILE_LABEL,
  type HomeRegion,
} from './homeOrder';
import { hydratePreferences } from './preferences';
import { useTileDrag } from './tileDrag';

export function MarketRail({
  result,
}: {
  readonly result: ServerData<{ readonly cards: readonly MarketCardView[] }>;
}): JSX.Element {
  const { t } = useLocale();
  const [sideOrder, setSideOrder] = useState<readonly string[]>(() => homeOrder('side'));
  const [hidden, setHidden] = useState<readonly string[]>(() => hiddenTiles());

  /*
   * THE ACCOUNT IS ASKED ONCE, ON MOUNT — the same effect the home screen runs for the main
   * region, and for the same reason: the first paint is this machine's answer and the account's
   * arrives a moment later. It is not a second mechanism; `hydratePreferences` answers with the
   * keys that CHANGED and an empty answer sets no state.
   */
  useEffect(() => {
    let live = true;
    void hydratePreferences().then((changed) => {
      if (!live || changed.length === 0) return;
      if (changed.includes('home.order.side')) setSideOrder(homeOrder('side'));
      if (changed.includes('home.hidden')) setHidden(hiddenTiles());
    });
    return () => {
      live = false;
    };
  }, []);

  /*
   * ITS OWN DRAG CONTROLLER, AND THAT IS NOT A SECOND ANSWER TO *"WHAT IS BEING CARRIED"*. A drop
   * never crosses a region boundary — `tileDrag.ts` enforces it on `data-tile-region` — so the
   * two regions were never one gesture. They are now in two components, which makes that explicit
   * rather than merely true.
   */
  const applyDrop = useCallback((region: HomeRegion, dragged: string, target: string): void => {
    if (region === 'side') setSideOrder(placeTileAt('side', dragged, target));
  }, []);
  const drag = useTileDrag(applyDrop);

  const sideTile = {
    handleHe: t('home.moveHandle'),
    region: 'side',
    order: sideOrder,
    hidden,
    onMove: (id: string, delta: -1 | 1): void => setSideOrder(moveTile('side', id, delta)),
    drag,
  } as const;

  return (
    /*
      `<aside>`, because it is reference material beside the work rather than a second stream of
      it -- which is what a screen reader needs to be told and what `<div>` does not say.
    */
    <aside className="gp-w-side" aria-label={t('home.sideRegion')}>
      <Tile id="fx" labelHe={t(TILE_LABEL.fx)} {...sideTile}>
        <MarketTile titleHe={t('home.widgets.fx')} icon="repeat" result={result} domain="fx" />
      </Tile>

      <Tile id="rates" labelHe={t(TILE_LABEL.rates)} {...sideTile}>
        <MarketTile
          titleHe={t('home.widgets.rates')}
          icon="chart-column"
          result={result}
          domain="policy_rate"
        />
      </Tile>

      <Tile id="cpi" labelHe={t(TILE_LABEL.cpi)} {...sideTile}>
        <MarketTile
          titleHe={t('home.widgets.cpi')}
          icon="trending-down"
          result={result}
          domain="cpi"
        />
      </Tile>

      <Tile id="fuel" labelHe={t(TILE_LABEL.fuel)} {...sideTile}>
        <MarketTile titleHe={t('home.widgets.fuel')} icon="tag" result={result} domain="fuel" />
      </Tile>
    </aside>
  );
}
